import { FormikProps } from 'formik';
import { Dispatch } from 'redux';
import { Col, Input, Label, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { useTranslation } from 'react-i18next';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FluidContainerClean from 'Components/pos2u/Wrappers/FluidContainer';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import {
  setCurrentPage,
  setEntriesPerPage,
  setInputPageValue,
} from 'slices/productInventory/reducer';

import PointsHistoryTable from 'pages/Customers/CustomerPointsHistoryModal/PointsHistoryTable';

import {
  CustomerPointsModalValue,
  PointsHistoryItem,
} from 'types/customer/customerTypes';
import { getPointsHistoryList } from 'services/customerService';
import { mapPointsType } from 'utils/mappers/customerMapper';

const columnWhiteSpace = 'normal';

const PointsHistoryForm = ({
  dispatch,
  formik,
}: {
  dispatch: Dispatch;
  formik: FormikProps<CustomerPointsModalValue>;
}) => {
  const { t } = useTranslation();
  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';

  const pointsHistoryState = useSelector(
    (state: RootState) => state.Customer.PointsHistoryModal
  );
  const {
    // PointsHistoryModalOpen,
    // countSubmit,
    // PointsHistoryValues,
    activePointsHistoryIndex,
    PageDetails,
  } = pointsHistoryState;

  const columnHelper = createColumnHelper<PointsHistoryItem>();

  const [PointsHistoryList, setPointsHistoryList] = useState<
    PointsHistoryItem[]
  >([]);
  const [PointsHistoryListTotal, setPointsHistoryListTotal] =
    useState<number>(0);

  const isFetched = useRef(false);

  const fetchPointsHistoryData = useCallback(async () => {
    if (!activePointsHistoryIndex) return;
    if (isFetched.current) return;

    isFetched.current = true;

    const controller = new AbortController();
    const signal = controller.signal;
    try {
      const rs = await getPointsHistoryList(
        activePointsHistoryIndex,
        PageDetails.currentPage + 1
      );
      const { message, error_code, data } = rs;
      if (message === 'OK' && error_code === 0) {
        const { total, points_history } = data;
        setPointsHistoryListTotal(total);

        setPointsHistoryList(points_history);
      } else {
        setPointsHistoryListTotal(0);
        setPointsHistoryList([]);
      }
    } catch (error) {
      if (!signal.aborted) {
        console.error('Failed to fetch Inventory History data:', error);
      } else {
        console.log('Request aborted');
      }
    } finally {
      isFetched.current = false;
    }
    return () => controller.abort();
  }, [activePointsHistoryIndex, PageDetails.currentPage]);

  useEffect(() => {
    fetchPointsHistoryData();
  }, [fetchPointsHistoryData]);

  const PointsHistoryColumns: ColumnDef<PointsHistoryItem, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: `created_date`,
        header: () => <div className="text-center">{t('Date')}</div>,
        cell: (cell) => (
          <div id={`created_date-${cell.row.index}`}>
            <span>{cell.row.original.created_date}</span>
          </div>
        ),
        meta: {},

        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `sale_id`,
        header: () => <div className="text-center">{t('Invoice ID')}</div>,

        cell: (cell) => (
          <div id={`sale_id-${cell.row.index}`}>
            <span>{cell.row.original.sale_id}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `source_ref`,
        header: () => <div className="text-center">{t('Types')}</div>,

        cell: (cell) => (
          <div id={`source_ref-${cell.row.index}`} className={`text-center`}>
            <span>{mapPointsType(cell.row.original.source_ref, t)}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `point_amount`,
        header: () => <div className="text-center">{t('Points')}</div>,

        cell: (cell) => (
          <div id={`point_amount-${cell.row.index}`}>
            <span>{cell.row.original.point_amount}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `expired_date`,
        header: () => <div className="text-center">{t('Expire Date')}</div>,

        cell: (cell) => (
          <div id={`expired_date-${cell.row.index}`}>
            <span>
              {cell.row.original.expired_date !== ''
                ? cell.row.original.expired_date
                : '-'}
            </span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `username`,
        header: () => <div className="text-center">{t('Employee')}</div>,

        cell: (cell) => (
          <div id={`employee_id-${cell.row.index}`}>
            <span>{cell.row.original.username}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.display({
        id: `comment`,
        header: () => <div className="text-center">{t('Comments')}</div>,

        cell: (cell) => (
          <div id={`comment-${cell.row.index}`}>
            <span>{cell.row.original.comment}</span>
          </div>
        ),
        meta: {},
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [t, columnHelper]
  );

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };
  const totalPages = Math.ceil(
    PointsHistoryListTotal / PageDetails.entriesPerPage
  );
  return (
    <>
      <div className={`mb-lg-3 mb-xs-1 row`}>
        <Label htmlFor="#" className="col-sm-4 col-form-label">
          {t('Account Number')}
        </Label>
        <Col sm={8} className="d-flex align-items-center">
          <FluidContainerClean>
            <Input
              type="text"
              name={`accountNumber`}
              value={formik.values.info.account_number || ''}
              onChange={formik.handleChange}
              className="me-2"
              disabled
            />
          </FluidContainerClean>
        </Col>
      </div>
      <div className={`mb-lg-3 mb-xs-1 row`}>
        <Label htmlFor="#" className="col-sm-4 col-form-label">
          {t('Customer')}
        </Label>
        <Col sm={8} className="d-flex align-items-center">
          <FluidContainerClean>
            <Input
              type="text"
              name={`first_name`}
              value={formik.values.info.first_name || ''}
              onChange={formik.handleChange}
              className="me-2"
              disabled
            />
          </FluidContainerClean>
        </Col>
      </div>
      <div className={`mb-lg-3 mb-xs-1 row`}>
        <Label htmlFor="#" className="col-sm-4 col-form-label">
          {t('Points History')}
        </Label>
        <Col sm={8} className="d-flex align-items-center">
          <FluidContainerClean>
            <Input
              type="text"
              name={`name`}
              value={formik.values.info.point_balance || ''}
              onChange={formik.handleChange}
              className="me-2"
              disabled
            />
          </FluidContainerClean>
        </Col>
      </div>

      <div className={``}>
        <PointsHistoryTable
          columns={PointsHistoryColumns}
          data={PointsHistoryList || []}
          customPageSize={PageDetails.entriesPerPage}
          divClass="table-responsive table-card mb-1 mt-0 mx-auto"
          tableClass="align-middle table-nowrap"
          theadClass={`table-${
            isDarkMode ? 'dark' : 'light'
          } text-muted text-uppercase`}
          thClass={'px-1'}
          tdClass={'px-1'}
          TotalData={PointsHistoryListTotal}
          totalPages={totalPages}
          setEntriesPerPage={setEntriesPerPage}
          currentPage={PageDetails.currentPage}
          handlePageChange={handlePageChange}
          inputPageValue={PageDetails.inputPageValue}
          setInputPageValue={setInputPageValue}
          columnWhiteSpace={columnWhiteSpace}
        />
      </div>

      <Row>
        <Col xs={12} sm={12} className="text-center">
          <div className="mb-lg-3 mb-xs-1">
            <Row>
              <Col xs={0} sm={4}></Col>

              <Col xs={5} sm={3}>
                {/* <Button
                  block
                  color="success"
                  type="submit"
                  id="edit-price-save"
                  disabled={
                    InventoryDetailsModal.FormErrorCount !== 0 ||
                    formik.isSubmitting
                  }
                  // disabled={formik.isSubmitting}
                  onClick={async () => {
                    await formik.setTouched(
                      Object.keys(formik.initialValues).reduce(
                        (acc, key) => ({ ...acc, [key]: true }),
                        {}
                      )
                    );
                    dispatch(setShowAllValidatePriceErrors(true));
                    dispatch(setCountPriceSubmit());
                  }}
                >
                  {t('Btn_Save')}
                </Button> */}
              </Col>
              {/* <Col xs={1} sm={3}></Col> */}
            </Row>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default PointsHistoryForm;
